import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConfig } from '../config';
import { Stage } from '../config/ApplicationConfig';
import { isAllowedSignInEmail } from '../config/signInAllowlist';
import { useStaffSignOut } from './staffSignOut';
import { clearAgentDashboardTokens } from '../apps/apis/claudeCodeDashboard/instance';
import {
  AGENT_DASHBOARD_SESSION_STORAGE_KEY,
  CLAUDE_CODE_SESSION_STORAGE_KEY,
} from '../apps/apis/claudeCodeDashboard/session';

interface User {
  email: string;
  name: string;
  picture: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (userData: User) => void;
  logout: () => void;
  validateDomain: (email: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** How long the page may stay after the identity provider's sign-out settles before it is treated as failed. */
const SIGN_OUT_LEAVE_WAIT_MS = 3_000;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const staffSignOut = useStaffSignOut();

  const validateDomain = (email: string): boolean => {
    if (getConfig().stage === Stage.LOCAL) {
      return true;
    }
    return isAllowedSignInEmail(email);
  };

  useEffect(() => {
    // Initialize user from localStorage on component mount
    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);

        // Validate that the stored user has the correct domain
        if (!validateDomain(parsedUser.email)) {
          console.error('Invalid email domain in stored user. Logging out:', parsedUser.email);
          localStorage.removeItem('user');
          return;
        }

        console.log('Loaded user from localStorage:', parsedUser.email);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user from localStorage', error);
        localStorage.removeItem('user');
      }
    }
  }, []);

  const login = (userData: User) => {
    // Double-check domain before login
    if (!validateDomain(userData.email)) {
      console.error('Attempted login with unauthorized email domain:', userData.email);
      return;
    }

    console.log('Setting user in AuthProvider:', userData.email);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    console.log('Logging out user');
    localStorage.removeItem('user');
    clearAgentDashboardTokens();
    localStorage.removeItem(AGENT_DASHBOARD_SESSION_STORAGE_KEY);
    localStorage.removeItem(CLAUDE_CODE_SESSION_STORAGE_KEY);
    if (staffSignOut) {
      // Leaves for the identity provider's sign-out page, which sends the browser back to the console. The user is
      // kept in memory until then: dropping it would send the router to the login page while the provider is still
      // leaving, and the two navigations race.
      // A page still here shortly after falls back. The wait starts with the sign-out, not when it settles: the SDK
      // reports a failure by settling without leaving, but it can also hang on OIDC discovery and never settle.
      setTimeout(() => {
        setUser(null);
        navigate('/login');
      }, SIGN_OUT_LEAVE_WAIT_MS);
      void staffSignOut().catch(() => undefined);
      return;
    }
    setUser(null);
    navigate('/login');
  };

  const authContextValue = {
    user,
    isAuthenticated: Boolean(user),
    login,
    logout,
    validateDomain,
  };

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
